import { formatINR } from "@/lib/api";
import { useClinic } from "@/hooks/useClinic";

const fmtDate = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">{label}</div>
    {children}
  </div>
);

export default function ReceiptPrintable({ a, imgSrc, nodeId }) {
  const clinic = useClinic();
  return (
    <div id={nodeId} className="bg-white p-8 text-slate-800">
      <div className="flex items-start justify-between border-b-2 border-teal-600 pb-5">
        <div className="flex items-center gap-3">
          <img src="/logo-512.png" alt={clinic.name} data-testid="receipt-logo" className="h-14 w-14 object-contain" />
          <div>
            <div className="font-head text-2xl font-extrabold tracking-tight text-slate-900">{clinic.name}</div>
            <div className="text-xs text-slate-500" data-testid="receipt-clinic-contact">{clinic.address} • {clinic.phone}{clinic.email ? ` • ${clinic.email}` : ""}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider text-slate-400">Receipt No.</div>
          <div className="font-mono font-semibold text-teal-700">{a.receipt_no}</div>
          {a.reference_id && <div className="font-mono text-xs text-slate-500">Ref: {a.reference_id}</div>}
          <div className="text-xs text-slate-500 mt-1">{fmtDate(a.created_at)}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 mt-6">
        <Field label="Doctor">
          <div className="font-semibold text-slate-900">{a.doctor_name}</div>
          <div className="text-sm text-slate-500">{a.doctor_specialty}</div>
        </Field>
        <Field label="Patient">
          <div className="font-semibold text-slate-900">{a.patient_name}</div>
          <div className="text-sm text-slate-500">Age: {a.patient_age} yrs{a.phone ? ` · ${a.phone}` : ""}</div>
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-6 mt-5">
        <Field label="Appointment Date"><div className="text-sm font-medium">{fmtDate(a.appointment_date)}</div></Field>
        <Field label="Disease"><div className="text-sm font-medium">{a.disease_name || "—"}</div></Field>
      </div>

      <Field label="Diagnosis Comments" className="mt-5">
        <div className="text-sm bg-slate-50 border border-slate-200 rounded-lg p-3 min-h-[60px] whitespace-pre-wrap">{a.diagnosis || "—"}</div>
      </Field>

      <div className="mt-6 border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex justify-between items-center px-4 py-3 bg-slate-50 text-sm font-medium text-slate-600"><span>Description</span><span>Amount</span></div>
        <div className="flex justify-between items-center px-4 py-3 text-sm border-t"><span>Consultation Fee</span><span className="font-mono">{formatINR(a.amount)}</span></div>
        <div className="flex justify-between items-center px-4 py-3 bg-teal-50 border-t">
          <span className="font-semibold text-teal-900">Total Payable</span>
          <span data-testid="receipt-amount-display" className="font-mono font-bold text-lg text-teal-700">{formatINR(a.amount)}</span>
        </div>
      </div>

      {a.prescription_path && imgSrc && (
        <Field label="Prescription" className="mt-6">
          <img data-testid="receipt-prescription-image" src={imgSrc} alt="Prescription" className="max-h-80 w-auto rounded-lg border border-slate-200" />
        </Field>
      )}

      <div className="flex justify-between items-end mt-10 pt-6 border-t">
        <div className="text-xs text-slate-400">{clinic.footer_note}</div>
        <div className="text-center">
          <div className="h-10 border-b border-slate-400 w-40 mb-1" />
          <div className="text-xs text-slate-500">Authorised Signature</div>
        </div>
      </div>
    </div>
  );
}
